import { DesignTokens } from "melco-ui";
import styled from "styled-components";

interface machineStatusDisplayLookupResult {
  textId: string;
  bgColorId: string;
}

const { spacings, colors, elevations } = DesignTokens;

export const DisplayWrapper = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;

export const EmptyMachineGroupWrapper = styled.div`
  border: 1px dashed ${colors.gray.middle};
  padding: ${spacings.middle}px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export type MachineStatus =
  | "running"
  | "stopped"
  | "notconnected"
  | "error"
  | "na"
  | "threadbreak"
  | "booting"
  | "idle"
  | "hooplimits"
  | "maintenance";

const MACHINE_STATUS_COLOR_RUNNING = "#c0e4aa";
const MACHINE_STATUS_COLOR_ERROR = colors.feedback.error;
const MACHINE_STATUS_COLOR_NOT_CONNECTED = colors.feedback.warning;
const MACHINE_STATUS_COLOR_NEGATIVE = colors.main.negative;
const MACHINE_STATUS_COLOR_GRAY_MIDDLE = colors.gray.middle;
const MACHINE_STATUS_COLOR_GRAY_DARK = colors.gray.dark;

// Define TextLookup with a mapping of textIds to text values
export const textLookup: { [key: string]: string } = {
  running: "running",
  stopped: "stopped",
  error: "error",
  threadbreak: "threadbreak",
  booting: "booting",
  idle: "idle",
  hooplimits: "hooplimits",
  maintenance: "maintenance",
  manualstop: "manualstop",
  empty: ""
};

// Define TextLookup with a mapping of textIds to text values
export const textColorLookup: { [key: string]: string } = {
  rederror: MACHINE_STATUS_COLOR_NEGATIVE,
  idle: MACHINE_STATUS_COLOR_GRAY_DARK,
  notconnected: MACHINE_STATUS_COLOR_NEGATIVE,
};

// Define TextLookup with a mapping of textIds to text values
export const backgroundColorLookup: { [key: string]: string } = {
  running: MACHINE_STATUS_COLOR_RUNNING,
  yellowerror: "yellow",
  rederror: MACHINE_STATUS_COLOR_ERROR,
  idle: MACHINE_STATUS_COLOR_GRAY_MIDDLE,
  maintenance: MACHINE_STATUS_COLOR_GRAY_MIDDLE,
  startup: MACHINE_STATUS_COLOR_GRAY_MIDDLE,
  notconnected: MACHINE_STATUS_COLOR_NOT_CONNECTED,
};

// export const NOT_CONNECTED = {
//   textId: "notconnected",
//   bgColorId: "yellowerror",
// };

export const NA = {
  textId: "na",
};

// Lookup map based on combinations of Event Type and Machine Status
export const machineStatusDisplayLookup: {
  [key: string]: machineStatusDisplayLookupResult;
} = {
  LOAD_DESIGN: {
    textId: "idle",
    bgColorId: "idle",
  },
  MACHINE_STOP: {
    textId: "manualstop",
    bgColorId: "yellowerror",
  },
  "MACHINE_STOP-ESTOP": {
    textId: "error",
    bgColorId: "rederror",
  },
  FINISH_DESIGN: {
    textId: "idle",
    bgColorId: "idle",
  },
  RESET_DESIGN: {
    textId: "idle",
    bgColorId: "idle",
  },
  "STATUS_UPDATE-NORMAL-RUNNING-RUNNING_NORMAL": {
    textId: "running",
    bgColorId: "running",
  },
  MACHINE_START: { textId: "running", bgColorId: "running" },
  THREAD_BREAK: { textId: "threadbreak", bgColorId: "yellowerror" },
  CRITICAL_ERROR: { textId: "error", bgColorId: "rederror" },
  MAINTENANCE_ROUTINE_CANCELED: { textId: "idle", bgColorId: "idle" },
  MACHINE_STARTUP: { textId: "booting", bgColorId: "startup" },
  "STATUS_UPDATE-STARTUP-CONNECTING": {
    textId: "booting",
    bgColorId: "startup",
  },
  "STATUS_UPDATE-STARTUP-INITIALIZING": {
    textId: "booting",
    bgColorId: "startup",
  },
  "STATUS_UPDATE-STARTUP-STARTUP_ERROR": {
    textId: "error",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-NORMAL-STITCH_ZERO": { textId: "idle", bgColorId: "idle" },
  "STATUS_UPDATE-NORMAL-IDLE": { textId: "idle", bgColorId: "idle" },
  "STATUS_UPDATE-NORMAL-STITCH_ZERO-STITCH_ZERO_READY": {
    textId: "idle",
    bgColorId: "idle",
  },
  "STATUS_UPDATE-NORMAL-STITCH_ZERO-STITCH_ZERO_LIMITS_EXCEEDED": {
    textId: "hooplimits",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-NORMAL-STANDBY-STANDBY_READY": {
    textId: "manualstop",
    bgColorId: "yellowerror",
  },
  "STATUS_UPDATE-NORMAL-STANDBY-STANDBY_LIMITS_EXCEEDED": {
    textId: "hooplimits",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-NORMAL-STANDBY-STANDBY_MACHINE_STOPPED": {
    textId: "stopped",
    bgColorId: "yellowerror",
  },
  "STATUS_UPDATE-CRITICAL_ERROR-ERROR_ACTIVE": {
    textId: "error",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-CRITICAL_ERROR-ERROR_ESTOPPED": {
    textId: "error",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-ESTOPPED-ESTOPPED_RUNTIME": {
    textId: "error",
    bgColorId: "rederror",
  },
  "STATUS_UPDATE-ESTOPPED-ESTOPPED_STARTUP": {
    textId: "error",
    bgColorId: "rederror",
  },
  MAINTENANCE_ROUTINE_STARTED: {
    textId: "maintenance",
    bgColorId: "maintenance",
  },
  MAINTENANCE_ROUTINE_COMPLETED: {
    textId: "idle",
    bgColorId: "idle",
  },
  NOT_CONNECTED: {
    textId: "notconnected",
    bgColorId: "yellowerror",
  },
};

export const machineStatusIdToValue = (id: string) => {
  return machineStatusDisplayLookup[id];
};

export const machineStatusTextIdToValue = (id: string) => {
  return textLookup[id];
};

// Define a mapping of WebSocket error codes to their descriptions
const websocketErrorCodeMapping: { [key: number]: string } = {
  1000: "Normal Closure: The connection successfully completed the purpose for which it was created.",
  1001: "Going Away: The endpoint is going away, either because of a server failure or because the browser is navigating away from the page that opened the connection.",
  1002: "Protocol error: The endpoint is terminating the connection due to a protocol error.",
  1003: "Unsupported Data: The connection is being terminated because the endpoint received data of a type it cannot accept.",
  1005: "No Status Rcvd: Reserved. Indicates that no status code was provided even though one was expected.",
  1006: "Abnormal Closure: Indicates that a connection was closed abnormally (with no close frame being sent).",
  1007: "Invalid frame payload data: The endpoint is terminating the connection because a message was received that contained inconsistent data (e.g., non-UTF-8 data within a text message).",
  1008: "Policy Violation: The endpoint is terminating the connection because it received a message that violates its policy.",
  1009: "Message Too Big: The endpoint is terminating the connection because a data frame was received that is too large.",
  1010: "Mandatory Extension: The client is terminating the connection because it expected the server to negotiate one or more extension, but the server didn’t.",
  1011: "Internal Error: The server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.",
  1012: "Service Restart: The server is terminating the connection because it is restarting.",
  1013: "Try Again Later: The server is terminating the connection due to a temporary condition, e.g. it is overloaded.",
  1014: "Bad Gateway: The server was acting as a gateway or proxy and received an invalid response from the upstream server.",
  1015: "TLS handshake: Indicates that the connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can’t be verified).",
};

// Define the function to handle the error and return details
export const websocketError = (code: number): string => {
  return websocketErrorCodeMapping[code] || "Unknown error code";
};
